// Ekran kilitleri için saf yardımcılar (React'e bağlı değil, `node --test` ile sınanır).
// `access` yoksa (eski sunucu / hata) eski `tenantPlan` mantığı aynen uygulanır.
import type { Access } from '../../../supabase/functions/_shared/plan-access.ts';

/** Ücretsiz paketin günlük WhatsApp sınırı (sunucu: wa-plan.ts WA_FREE_DAILY_LIMIT) */
export const WA_FREE_DAILY_LIMIT = 5;

type PlanLike = Pick<Access, 'tier' | 'plan'> | null | undefined;

/** Eski `tenantPlan !== 'starter'` kontrolü (Professional ve üstü). Ücretsiz pakette hep false. */
export function isProPlan(access: PlanLike, tenantPlan: string | null | undefined): boolean {
  if (!access) return (tenantPlan || 'starter') !== 'starter';
  if (access.tier === 'free') return false;
  return access.plan !== 'starter' && access.plan !== 'free';
}

/** Eski `tenantPlan === 'enterprise'` kontrolü. Ücretsiz pakette hep false. */
export function isEnterprisePlan(access: PlanLike, tenantPlan: string | null | undefined): boolean {
  if (!access) return tenantPlan === 'enterprise';
  return access.tier !== 'free' && access.plan === 'enterprise';
}

/** Otomatik WhatsApp gönderimi günlük sınırda ya da hediye kontör bitince atlandı mı (sunucu 200 `{success:false, skipped:'PLAN_LIMIT', feature?}`) */
export function isWaPlanSkip(result: unknown): boolean {
  return !!result && typeof result === 'object' && (result as { skipped?: unknown }).skipped === 'PLAN_LIMIT';
}

/**
 * Atlanan otomatik gönderimde "Satın al" penceresini açar ('plan-limit' olayı, yanıt başına bir kez).
 * Yanıt atlanmadıysa hiçbir şey yapmaz. Dönüş: olay yayıldı mı.
 */
export function notifyWaPlanSkip(
  result: unknown,
  target: Pick<EventTarget, 'dispatchEvent'> | undefined = typeof window !== 'undefined' ? window : undefined,
): boolean {
  if (!isWaPlanSkip(result)) return false;
  const feature = (result as { feature?: unknown }).feature === 'whatsapp_credits' ? 'whatsapp_credits' : 'whatsapp_daily';
  const detail = feature === 'whatsapp_daily' ? { feature, limit: WA_FREE_DAILY_LIMIT } : { feature };
  target?.dispatchEvent(new CustomEvent('plan-limit', { detail }));
  return true;
}

/** Ücretsiz pakette çalışabilen roller: patron ve gizli destek hesabı (R7). */
export function isOwnerRole(role: string | null | undefined): boolean {
  const r = String(role || '').toLowerCase();
  return r === 'patron' || r === 'admin';
}

/**
 * Sipariş ekranı tam sayfa kilidi. Yalnız salt okunur / kapalı hesapta (ücretsiz, deneme, ücretli, ek süre açık).
 * `access` yoksa (eski sunucu / hata) eski kural: bitiş tarihi geçtiyse kilit — paket durumu yüklendikten sonra.
 */
export function orderScreenLocked(access: Pick<Access, 'tier'> | null | undefined, legacyExpired: boolean, planLoaded: boolean): boolean {
  if (access) return access.tier === 'read_only' || access.tier === 'closed';
  return planLoaded && legacyExpired;
}

/**
 * Kapalı hesapta gösterilecek ekran: patron/destek → abonelik sayfası (ödeme yapabilir),
 * diğer roller → sabit "Hesap kapalı" ekranı (abonelik sayfası onları ana sayfaya atar → döngü olurdu).
 * Kapalı değilse ya da `access` yoksa null.
 */
export function closedAccountView(access: Pick<Access, 'tier'> | null | undefined, role: string | null | undefined): 'subscription' | 'staff_locked' | null {
  if (!access || access.tier !== 'closed') return null;
  return isOwnerRole(role) ? 'subscription' : 'staff_locked';
}

/**
 * 402 ayrıntısı ücretsiz pakette patron dışı oturumun durdurulması mı (sunucu ara katmanı: feature 'users', reason 'free_role').
 * Böyleyse oturum kapatılır (personel satın alamaz, pencere açılmaz). Kullanıcı ekleme sınırı (reason yok) pencere olarak kalır.
 */
export function isFreeRoleBlock(detail: { feature?: unknown; reason?: unknown } | null | undefined, role: string | null | undefined): boolean {
  if (!detail || detail.feature !== 'users' || detail.reason !== 'free_role') return false;
  return !isOwnerRole(role);
}
